import { reactive, computed } from "vue";
import { useToast } from "primevue/usetoast";
import { focusFirstInvalidInput, scrollIntoView, scrollToTop, setFocus, focusFirstEnabledInput, focusErrorMessage } from '@/utils/pageUtils';
import { TOAST_MESSAGE_SEVERITY, TOAST_SAVE_LIFE } from "@/utils/constants";
import { useAppStore } from '@/stores/appStore';
import { useRouter } from "vue-router";

/**
 * Composable function providing helper methods for page state management.
 * @returns {Object} Page helper methods and state.
 */
export function usePageHelper() {
  const router = useRouter();
  const toast = useToast();
  const appStore = useAppStore();
  const state = reactive({
    isLoading: false,
    isProcessing: false,
    message: 'Loading...',
    isSaving: false,
    hasError: false,
    errorMessage: '',
  });

  /**
   * Set the loading state with a custom message.
   * @param {string} message - The loading message to display.
   */
  const setLoadingState = (message) => {
    state.isLoading = true;
    state.isProcessing = true;
    state.message = message;
    appStore.showLoading(message);
    clearError();
  };

  const setErrorMessage = (message) => {
    state.hasError = true;
    state.errorMessage = message;
  };

  /**
   * Set the saving state with a custom message.
   * @param {string} message - The saving message to display.
   */
  const setSavingState = (message) => {
    state.isSaving = true;
    state.isProcessing = true;
    state.message = message;
    appStore.showLoading(message);
    clearError();
  };

  /**
   * Set the error state based on the provided error object.
   * @param {Object} error - The error object.
   */
  const setErrorState = (error) => {
    let message = error.message;

    appStore.logError(error); // Log the error to the server
    // Check if the error has a response (i.e., the server responded with a status code)
    if (error.response) {
      const statusCode = error.response.status;

      if (statusCode === 401) {
        // Handle 401 Unauthorized
        message = 'Unauthorized.';
      } else {
        // Handle other status codes
        message = `Error while processing request.`;
      }
    } else if (error.request) {
      // The request was made but no response was received
      message = 'No response from server. Please check your network.';
    }  

    appStore.showError(message);
    triggerError(message);
  };

  /**
   * Hide the loading or saving state.
   */
  const hideWaitingState = () => {
    appStore.hideLoading();
    state.isProcessing = false;
    state.isLoading = false;
    state.isSaving = false;
  };

  /**
   * Trigger an error state with a custom message.
   * @param {string} message - The error message to display.
   */
  const triggerError = (message) => {
    state.hasError = true;
    state.errorMessage = message;
  };

  /**
   * Clear the error state.
   */
  const clearError = () => {
    state.hasError = false;
    state.errorMessage = '';
    appStore.hideError();
  };

    /**
   * Display a toast notification.
   * @param {string} detail - The detail message of the toast.
   * @param {string} [severity=TOAST_MESSAGE_SEVERITY.Success] - The severity level of the toast. 
   * Possible values: 'Success', 'Info', 'Warn', 'Error'.
   * @param {string} [summary='Success'] - The summary title of the toast.
   */
  const displayToast = (detail, severity = TOAST_MESSAGE_SEVERITY.Success, summary = 'Success') => {
    toast.add({ group: 'top-toast', severity: severity, summary: summary, detail: detail, life: TOAST_SAVE_LIFE });
  };

  /**
   * Execute a save function with error handling and state management.
   * @param {Function} saveFunction - The function to execute for saving.
   * @param {string} [message='Saving...'] - The saving message to display.
   * @param {boolean} [showToast=false] - Whether to show a toast notification.
   * @param {string} [toastMessage='Operation was successful.'] - The toast message to display.
   * @returns {Promise<any>} The result of the save function.
   */
  const save = async (saveFunction, message = 'Saving...', showToast = false, toastMessage = 'Operation was successful.') => {
    setSavingState(message);
    try {
      const result = await saveFunction();
      if (showToast) {
        displayToast(toastMessage, TOAST_MESSAGE_SEVERITY.Success, 'Success');
      }
      return result;
    } catch (error) {
      setErrorState(error);
    } finally {
      hideWaitingState();
    }
  };

  /**
   * Execute a fetch function with error handling and state management.
   * @param {Function} fetchFunction - The function to execute for fetching data.
   * @param {string} [message='Loading...'] - The loading message to display.
   * @param {boolean} [showLoading=true] - Whether to show the loading state.
   * @returns {Promise<void>}
   */
  const fetch = async (fetchFunction, message = 'Loading...', showLoading = true) => {
    if (showLoading) {
      setLoadingState(message);
    }
    try {
      await fetchFunction();
    } catch (error) {
      setErrorState(error);
    } finally {
      hideWaitingState();
    }
  };

  const navigateToRouteName = (routeName, params = {}) => {
    const route = { name: routeName, params };
    router.push(route);
  };

  const showLoading = computed(() => state.isLoading || state.isSaving);

  return {
    state,

    // Computed properties
    showLoading,

    fetch,
    save,
    showToast: displayToast,
    scrollIntoView,
    setFocus,
    focusFirstInvalidInput,
    focusFirstEnabledInput,
    focusErrorMessage,
    scrollToTop,
    triggerError,
    clearError,
    setErrorMessage,
    navigateToRouteName,
  };
}